import { createClient } from '@supabase/supabase-js';

// Variables d'environnement (configurées dans Vercel)
// Si les variables d'environnement existent, on les utilise (STAGING ou PROD via Vercel)
// Sinon on utilise les valeurs PROD en fallback (sécurité)
const SUPABASE_URL = 
  process.env.REACT_APP_SUPABASE_URL ||
  'https://nmmpxujcbdcnpdryctks.supabase.co';

const SUPABASE_ANON_KEY = 
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5tbXB4dWpjYmRjbnBkcnljdGtzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYxMTkzODMsImV4cCI6MjA5MTY5NTM4M30.cZl9zLjPQYeRG2y9vEn3PMsWuUzZymDcXypcF8vZq-8';

// Auth : flux PKCE (le défaut implicit renvoie les jetons Google dans le fragment
// d'URL #access_token, perdu quand le splash/landing renavigue au retour OAuth →
// la session Google ne persiste pas alors que email/mdp oui). PKCE renvoie un
// ?code= échangé puis persisté de façon fiable. persistSession/autoRefreshToken/
// detectSessionInUrl explicites (= défauts) pour verrouiller l'intention.
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    flowType: "pkce",
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

// Client secondaire dédié UNIQUEMENT à l'envoi de l'email de reset mot de passe.
// En flux PKCE, resetPasswordForEmail stocke un code_verifier en localStorage du
// navigateur qui a DEMANDÉ le reset ; si l'utilisateur ouvre le lien ?code= dans
// un AUTRE navigateur (webmail, appli mail), le verifier manque → "bad_code_verifier"
// → "Lien expiré ou invalide". En flux implicit, aucun verifier n'est stocké : le
// lien revient avec les jetons dans le #fragment (#access_token&type=recovery),
// que le client principal (detectSessionInUrl) échange partout → marche cross-device.
// persistSession off : ce client ne sert qu'à POSTer la demande, jamais à porter une session.
export const supabaseResetSender = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    flowType: "implicit",
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
    storageKey: "sb-hc-reset-sender",
  },
});

// IDs des utilisateurs à masquer pour l'utilisateur courant : ceux qu'il a bloqués
// ET ceux qui l'ont bloqué (blocage bidirectionnel), via la fonction RPC hidden_user_ids.
// Renvoie un Set vide si non connecté ou en cas d'erreur.
export async function getBlockedIds() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return new Set();
    const { data, error } = await supabase.rpc("hidden_user_ids");
    if (error) throw error;
    return new Set((data || []).map(id => typeof id === "string" ? id : id?.hidden_user_ids).filter(Boolean));
  } catch (e) {
    console.error("getBlockedIds:", e);
    return new Set();
  }
}

// Identifiant de session anonyme (stable par navigateur) pour dédupliquer le tunnel d'inscription.
function getSignupSessionId() {
  try {
    let sid = localStorage.getItem("hc_sid");
    if (!sid) {
      sid = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));
      localStorage.setItem("hc_sid", sid);
    }
    return sid;
  } catch (e) {
    return null;
  }
}

// Détecte les bots / robots d'indexation (Googlebot, crawlers, previews, headless…)
// pour NE PAS les compter dans les stats visiteurs -> chiffres = vrais humains.
export function isBot() {
  try {
    if (navigator.webdriver === true) return true;
    const ua = (navigator.userAgent || "").toLowerCase();
    return /bot|crawl|spider|slurp|mediapartners|adsbot|googlebot|bingbot|bingpreview|yandex|baiduspider|duckduckbot|sogou|exabot|facebookexternalhit|facebot|ia_archiver|semrush|ahrefs|mj12bot|dotbot|petalbot|applebot|seznambot|embedly|quora|whatsapp|telegrambot|discordbot|slackbot|twitterbot|linkedinbot|pinterest|redditbot|headless|phantomjs|puppeteer|playwright|lighthouse|gtmetrix|pingdom|uptimerobot|prerender|python-requests|curl|wget|axios|node-fetch|scrapy|monitoring/.test(ua);
  } catch (e) { return false; }
}

// Tunnel d'inscription (point B) : enregistre une étape ('opened' | 'account_created').
// Fire-and-forget : n'interrompt jamais l'UX, échoue silencieusement si la table n'existe pas.
export async function trackSignupEvent(step, lang) {
  try {
    await supabase.from("signup_events").insert({ step, session_id: getSignupSessionId(), lang: lang || null });
  } catch (e) {
    // silencieux : le suivi ne doit jamais bloquer l'inscription
  }
}

// Trafic app (mode découverte inclus) : enregistre une ouverture d'app, une seule fois par session
// navigateur. Déclenché au démarrage, avant tout login → capte aussi les visiteurs non connectés.
export async function trackAppOpen(lang) {
  try {
    if (isBot()) return;
    if (sessionStorage.getItem("hc_app_open_tracked") === "1") return;
    sessionStorage.setItem("hc_app_open_tracked", "1");
    await supabase.from("signup_events").insert({ step: "app_opened", session_id: getSignupSessionId(), lang: lang || null });
  } catch (e) {
    // silencieux : le suivi ne doit jamais bloquer l'app
  }
}

// Détecte le navigateur / la source du visiteur depuis le user-agent.
// Priorité aux navigateurs INTÉGRÉS (Insta/FB/TikTok/X…) car ils cassent
// l'inscription (OAuth/captcha/PWA). Ordre important (Chrome contient "safari").
export function detectVisitorSource() {
  try {
    const ua = (navigator.userAgent || "").toLowerCase();
    // Navigateurs intégrés des réseaux (in-app)
    if (ua.includes("instagram")) return "instagram";
    if (ua.includes("fban") || ua.includes("fbav") || ua.includes("fb_iab")) return "facebook";
    if (ua.includes("tiktok") || ua.includes("musical_ly") || ua.includes("bytedance") || ua.includes("bytelo")) return "tiktok";
    if (ua.includes("twitter")) return "x";
    if (ua.includes("snapchat")) return "snapchat";
    if (ua.includes("pinterest")) return "pinterest";
    if (ua.includes("linkedinapp")) return "linkedin";
    if (ua.includes("gsa/")) return "google-app";
    // Appli installée (TWA Android / PWA iOS)
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return "app";
    if (window.navigator && window.navigator.standalone === true) return "app";
    // Navigateurs classiques
    if (ua.includes("edg/") || ua.includes("edgios")) return "edge";
    if (ua.includes("samsungbrowser")) return "samsung";
    if (ua.includes("firefox") || ua.includes("fxios")) return "firefox";
    if (ua.includes("opr/") || ua.includes("opera") || ua.includes("opt/")) return "opera";
    if (ua.includes("crios") || ua.includes("chrome")) return "chrome";
    if (ua.includes("safari")) return "safari";
    return "other";
  } catch (e) { return "other"; }
}

// Appareil / OS du visiteur (avec quoi il se connecte).
export function detectDeviceOS() {
  try {
    const ua = (navigator.userAgent || "").toLowerCase();
    if (/iphone|ipod/.test(ua)) return "iphone";
    if (/ipad/.test(ua) || (ua.includes("macintosh") && navigator.maxTouchPoints > 1)) return "ipad";
    if (ua.includes("android")) return "android";
    if (ua.includes("windows")) return "windows";
    if (ua.includes("macintosh") || ua.includes("mac os")) return "mac";
    if (ua.includes("linux")) return "linux";
    return "other";
  } catch (e) { return "other"; }
}

// Source + appareil : 1×/session, pour tous (connectés + invités).
// Insère 'src_<navigateur>' ET 'dev_<os>' (les 2 viennent du user-agent).
export async function trackVisitorSource(lang) {
  try {
    if (isBot()) return;
    if (sessionStorage.getItem("hc_src_tracked") === "1") return;
    sessionStorage.setItem("hc_src_tracked", "1");
    const sid = getSignupSessionId();
    await supabase.from("signup_events").insert([
      { step: "src_" + detectVisitorSource(), session_id: sid, lang: lang || null },
      { step: "dev_" + detectDeviceOS(), session_id: sid, lang: lang || null },
    ]);
  } catch (e) {
    // silencieux
  }
}

// Pays du visiteur (depuis où) : géo-IP gratuit (ipwho.is, sans clé, niveau PAYS
// uniquement -> anonyme/RGPD OK). 1×/session. Insère 'geo_<code_pays>'.
export async function trackVisitorCountry(lang) {
  try {
    if (isBot()) return;
    if (sessionStorage.getItem("hc_geo_tracked") === "1") return;
    sessionStorage.setItem("hc_geo_tracked", "1");
    const res = await fetch("https://ipwho.is/");
    const data = await res.json();
    const cc = data && data.country_code ? String(data.country_code).toLowerCase() : "";
    if (!/^[a-z]{2}$/.test(cc)) return;
    await supabase.from("signup_events").insert({ step: "geo_" + cc, session_id: getSignupSessionId(), lang: lang || null });
  } catch (e) {
    // silencieux : le géo-IP ne doit jamais bloquer l'app
  }
}

// Répartitions visiteurs (admin) : navigateur + appareil + pays, en 1 RPC.
export async function getVisitorBreakdowns() {
  try {
    const { data, error } = await supabase.rpc("admin_visitor_breakdowns");
    if (error) throw error;
    return data || null;
  } catch (e) {
    console.error("getVisitorBreakdowns:", e);
    return null;
  }
}

// ---------- Upload vidéo (Cloudinary, signé) ----------
// 1) demande une signature à l'Edge Function sign-upload (connectés only),
// 2) POST le fichier à Cloudinary (compression/transcodage auto côté Cloudinary).
// Retourne { url, publicId } ou lève une erreur. onProgress(0-100) optionnel.
export async function uploadVideoCloudinary(file, onProgress) {
  const { data, error } = await supabase.functions.invoke("sign-upload", { body: {} });
  if (error || !data || !data.signature) throw new Error("sign_failed");
  const form = new FormData();
  form.append("file", file);
  form.append("api_key", data.apiKey);
  form.append("timestamp", String(data.timestamp));
  form.append("folder", data.folder);
  form.append("signature", data.signature);
  return await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `https://api.cloudinary.com/v1_1/${data.cloudName}/video/upload`);
    xhr.upload.onprogress = (e) => { if (onProgress && e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      try {
        const res = JSON.parse(xhr.responseText);
        if (res.secure_url) resolve({ url: res.secure_url, publicId: res.public_id });
        else reject(new Error((res.error && res.error.message) || "upload_failed"));
      } catch (e) { reject(e); }
    };
    xhr.onerror = () => reject(new Error("network"));
    xhr.send(form);
  });
}

// Miniature (poster) d'une vidéo Cloudinary : 1re image en JPG.
export function cloudinaryPoster(videoUrl) {
  try {
    if (!videoUrl || !/res\.cloudinary\.com/.test(videoUrl)) return null;
    return videoUrl.replace("/upload/", "/upload/so_0/").replace(/\.(mp4|mov|webm|m4v|avi|mkv)(\?.*)?$/i, ".jpg");
  } catch (e) { return null; }
}

// Découverte invités : trace ce qu'un visiteur NON connecté explore (HorseRoad,
// annonce, carte, annuaire) + les fois où il bute sur le mur d'inscription.
// 1×/session/step (dedup sessionStorage) -> ne gonfle pas la table.
export async function trackGuestEvent(step, lang) {
  try {
    if (isBot()) return;
    const key = "hc_ge_" + step;
    if (sessionStorage.getItem(key) === "1") return;
    sessionStorage.setItem(key, "1");
    await supabase.from("signup_events").insert({ step, session_id: getSignupSessionId(), lang: lang || null });
  } catch (e) {
    // silencieux : le suivi ne doit jamais bloquer l'app
  }
}

// Stats d'inscription pour l'admin (points A + B), via RPC sécurisée admin_signup_stats.
// Renvoie null si non autorisé / fonction absente.
export async function getSignupStats() {
  try {
    const { data, error } = await supabase.rpc("admin_signup_stats");
    if (error) throw error;
    return data || null;
  } catch (e) {
    console.error("getSignupStats:", e);
    return null;
  }
}

// Compteurs de l'annuaire Pro pour l'admin (fiches actives, créées par les
// utilisateurs, amorçage, réclamées, supprimées), via RPC admin_pro_stats.
export async function getAdminProStats() {
  try {
    const { data, error } = await supabase.rpc("admin_pro_stats");
    if (error) throw error;
    return data || null;
  } catch (e) {
    console.error("getAdminProStats:", e);
    return null;
  }
}

// Liste des utilisateurs n'ayant pas confirmé leur email (admin uniquement).
// Renvoie [] si non autorisé / fonction absente.
export async function getUnconfirmedUsers() {
  try {
    const { data, error } = await supabase.rpc("admin_unconfirmed_users");
    if (error) throw error;
    return data || [];
  } catch (e) {
    console.error("getUnconfirmedUsers:", e);
    return [];
  }
}

// Renvoyer le mail de confirmation à un utilisateur non confirmé (admin uniquement).
// Passe par l'Edge Function admin-resend-confirmation (clé service_role, pas de captcha).
// Retourne { ok: true } ou { ok: false, error: "..." }.
export async function adminResendConfirmation({ email, user_id }) {
  try {
    const redirectTo = (typeof window !== "undefined") ? window.location.origin : undefined;
    const { data, error } = await supabase.functions.invoke("admin-resend-confirmation", {
      body: { email, user_id, redirect_to: redirectTo },
    });
    if (error) {
      // Sur un statut non-2xx, supabase-js ne donne que « Edge Function returned a non-2xx
      // status code » ; la vraie raison (« attends 60 s », captcha…) est dans le corps.
      let detail = null;
      try { const b = await error.context?.json?.(); detail = b?.detail || b?.error || null; } catch (_) {}
      return { ok: false, error: detail || error.message || "invoke_error" };
    }
    if (data && data.ok) return { ok: true };
    return { ok: false, error: (data && data.error) || "unknown_error" };
  } catch (e) {
    console.error("adminResendConfirmation:", e);
    return { ok: false, error: String(e?.message || e) };
  }
}

// ============================================================
//  FICHES PRO (annuaire) — cf. docs/ETUDE-PRO-FICHES.md
// ============================================================

// Récupère les fiches Pro actives (annuaire), avec filtres optionnels.
// filters: { metier, family, region, country, q } — tous optionnels.
// Colonnes lisibles par les visiteurs : email / téléphone / WhatsApp sont réservés aux membres
// (grants par colonne depuis le 17 oct. 2026) et servis par get_pro_contact.
export const PRO_PUBLIC_COLS = "id,name,family,metiers,country,region,city,address,zone,lat,lng,description,details,hours,pricing,languages,certifications,experience_years,photos,logo_url,cover_url,socials,website,status,moderation_status,source,claimed,verified,rating,review_count,translations,user_id,created_at,updated_at,deleted_at";
// ads a des grants par colonne et `phone` n'est lisible par personne : jamais select("*") sur ads.
export const ADS_COLS = "id,user_id,category,subcat,title,description,price,location,location_type,city,region,country,lat,lng,latitude,longitude,photos,videos,video_url,tags,tarifs,infras,boxes,prestations,age_range,horse_breed,horse_age,horse_sex,horse_discipline,horse_level,horse_height,coverage_regions,contact_phone,contact_email,contact_message,hide_from_map,status,moderation_status,moderation_reason,moderation_flags,moderation_score,moderation_checked_at,report_count,views,boosted,expires_at,translations,created_at,updated_at,deleted_at,deleted_by,deletion_reason";

export async function getProContact(proId) {
  try {
    const { data, error } = await supabase.rpc("get_pro_contact", { p_pro_id: proId });
    if (error) throw error;
    return (data && data[0]) || null;
  } catch (e) { return null; }
}

export async function getPros(filters = {}) {
  try {
    let query = supabase.from("pros").select(PRO_PUBLIC_COLS)
      .eq("status", "active").is("deleted_at", null)
      .order("verified", { ascending: false })
      .order("name", { ascending: true })
      .limit(5000);
    if (filters.metier) query = query.contains("metiers", [filters.metier]);
    if (filters.family) query = query.eq("family", filters.family);
    if (filters.country) query = query.eq("country", filters.country);
    if (filters.region) query = query.eq("region", filters.region);
    const { data, error } = await query;
    if (error) throw error;
    let list = data || [];
    if (filters.q) {
      const q = filters.q.toLowerCase();
      list = list.filter(p =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.city || "").toLowerCase().includes(q) ||
        (p.description || "").toLowerCase().includes(q)
      );
    }
    return list;
  } catch (e) {
    console.error("getPros:", e);
    return [];
  }
}

// Récupère une fiche Pro par id.
export async function getProById(id) {
  try {
    const { data, error } = await supabase.from("pros").select(PRO_PUBLIC_COLS)
      .eq("id", id).is("deleted_at", null).maybeSingle();
    if (error) throw error;
    return data || null;
  } catch (e) {
    console.error("getProById:", e);
    return null;
  }
}

// Fiche(s) Pro de l'utilisateur connecté (pour l'édition).
export async function getMyPros() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];
    const { data, error } = await supabase.from("pros").select("*")
      .eq("user_id", user.id).is("deleted_at", null).order("created_at", { ascending: false });
    if (error) throw error;
    return data || [];
  } catch (e) {
    console.error("getMyPros:", e);
    return [];
  }
}

// Réclamer une fiche Pro pré-créée (non encore gérée). RPC SECURITY DEFINER :
// n'aboutit que si la fiche est libre (user_id IS NULL, claimed=false) et l'appelant connecté.
// Renvoie true si la fiche a bien été rattachée à l'utilisateur.
export async function claimPro(proId) {
  try {
    const { data, error } = await supabase.rpc("claim_pro", { pro_id: proId });
    if (error) throw error;
    return data === true;
  } catch (e) {
    console.error("claimPro:", e);
    return false;
  }
}

// Réclamation d'une fiche d'amorçage avec la clé secrète du mail (seules ces fiches sont réclamables).
export async function claimProWithKey(proId, key) {
  try {
    const { data, error } = await supabase.rpc("claim_pro_with_key", { p_pro_id: proId, p_key: key });
    if (error) throw error;
    return data === true;
  } catch (e) {
    console.error("claimProWithKey:", e);
    return false;
  }
}

// Anti-doublon : une fiche d'amorçage non réclamée porte-t-elle DÉJÀ l'email vérifié du compte ?
// Sans ça, un pro démarché qui s'inscrit puis crée sa fiche se retrouve avec DEUX fiches publiques.
export async function findUnclaimedProForMe() {
  try {
    const { data, error } = await supabase.rpc("find_unclaimed_pro_for_me");
    if (error) throw error;
    return (data && data[0]) || null;
  } catch (e) {
    console.error("findUnclaimedProForMe:", e);
    return null;
  }
}

// Reprise de cette fiche sans la clé du mail : l'email du compte (vérifié) doit être
// exactement celui publié sur la fiche — la preuve arrive dans la même boîte que la clé.
export async function claimProByEmail(proId) {
  try {
    const { data, error } = await supabase.rpc("claim_pro_by_email", { p_pro_id: proId });
    if (error) throw error;
    return data === true;
  } catch (e) {
    console.error("claimProByEmail:", e);
    return false;
  }
}

// Retrait d'une fiche d'amorçage par le destinataire du mail (la clé fait office de preuve, sans compte).
// `reason` = réponse facultative du mini-sondage (valeur fermée, ignorée côté SQL si hors liste).
export async function removeProByKey(proId, key, reason) {
  try {
    const params = { p_pro_id: proId, p_key: key };
    if (reason) params.p_reason = reason;
    const { data, error } = await supabase.rpc("remove_pro_by_key", params);
    if (error) throw error;
    return data === true;
  } catch (e) {
    console.error("removeProByKey:", e);
    return false;
  }
}

// Désinscription en un clic depuis le mail d'amorçage : retire la fiche ET bloque l'adresse
// pour tout futur démarchage (table outreach_suppression). La clé fait office de preuve.
export async function unsubscribeProByKey(proId, key) {
  try {
    const { data, error } = await supabase.rpc("outreach_unsubscribe_by_key", { p_pro_id: proId, p_key: key });
    if (error) throw error;
    return data === true;
  } catch (e) {
    console.error("unsubscribeProByKey:", e);
    return false;
  }
}

// ---------------------------------------------------------------------------
// RÉGLAGES GLOBAUX + OUTILS D'ADMINISTRATION
// ---------------------------------------------------------------------------

// Lecture publique volontaire : l'app doit connaître l'état AVANT toute connexion,
// sinon un visiteur ne verrait jamais le mode maintenance.
export async function getAppSettings() {
  try {
    const { data, error } = await supabase.from("app_settings").select("key, enabled");
    if (error) throw error;
    const out = {};
    (data || []).forEach(r => { out[r.key] = r.enabled; });
    return out;
  } catch (e) {
    // En cas d'échec on renvoie l'état le plus permissif : une panne de lecture
    // ne doit jamais fermer l'app par accident.
    return { maintenance: false, signups_open: true, free_ads: true, manual_moderation: false };
  }
}

export async function adminSetSetting(key, enabled) {
  try {
    const { error } = await supabase.rpc("admin_set_setting", { p_key: key, p_enabled: enabled });
    if (error) throw error;
    return true;
  } catch (e) { console.error("adminSetSetting:", e); return false; }
}

export async function adminSetUserPro(userId, isPro) {
  try {
    const { error } = await supabase.rpc("admin_set_user_pro", { p_user_id: userId, p_is_pro: isPro });
    if (error) throw error;
    return true;
  } catch (e) { console.error("adminSetUserPro:", e); return false; }
}

export async function adminRecentProReviews(limit = 50) {
  try {
    const { data, error } = await supabase.rpc("admin_recent_pro_reviews", { p_limit: limit });
    if (error) throw error;
    return data || [];
  } catch (e) { console.error("adminRecentProReviews:", e); return []; }
}

export async function adminDeleteProReview(id) {
  try {
    const { error } = await supabase.rpc("admin_delete_pro_review", { p_id: id });
    if (error) throw error;
    return true;
  } catch (e) { console.error("adminDeleteProReview:", e); return false; }
}

// ---------------------------------------------------------------------------
// AVIS SUR LES FICHES DE L'ANNUAIRE
// Les avis portent sur la FICHE (pro_id), pas sur un utilisateur : une fiche non
// réclamée peut donc être notée, ce qui donne au pro une raison de plus de la
// reprendre. La note moyenne et le compteur de pros sont recalculés par trigger.
// ---------------------------------------------------------------------------

// ⚠️ Les noms des auteurs viennent de profiles_public (lookup groupé) : lire
// profiles en direct pour afficher un AUTRE membre est bloqué par la RLS.
export async function getProReviews(proId) {
  try {
    const { data, error } = await supabase
      .from("pro_reviews")
      .select("id, pro_id, author_id, rating, comment, reply, replied_at, created_at")
      .eq("pro_id", proId)
      .order("created_at", { ascending: false });
    if (error) throw error;
    const rows = data || [];
    if (!rows.length) return [];
    const ids = [...new Set(rows.map(r => r.author_id))];
    const { data: profs } = await supabase
      .from("profiles_public")
      .select("id, prenom, nom, avatar_url, display_name, use_display_name, nom_ecurie, nom_pro")
      .in("id", ids);
    const byId = {};
    (profs || []).forEach(p => { byId[p.id] = p; });
    return rows.map(r => ({ ...r, author: byId[r.author_id] || null }));
  } catch (e) {
    console.error("getProReviews:", e);
    return [];
  }
}

// Un seul avis par personne et par fiche : la contrainte unique en base fait foi,
// on passe donc par un upsert plutôt que de gérer création et édition séparément.
export async function upsertProReview(proId, rating, comment) {
  try {
    const auth = await supabase.auth.getUser();
    const uid = auth?.data?.user?.id;
    if (!uid) return { ok: false, error: "not_signed_in" };
    const { error } = await supabase
      .from("pro_reviews")
      .upsert({ pro_id: proId, author_id: uid, rating, comment: comment || null }, { onConflict: "pro_id,author_id" });
    if (error) throw error;
    return { ok: true };
  } catch (e) {
    console.error("upsertProReview:", e);
    return { ok: false, error: e.message };
  }
}

export async function deleteProReview(proId) {
  try {
    const auth = await supabase.auth.getUser();
    const uid = auth?.data?.user?.id;
    if (!uid) return false;
    const { error } = await supabase.from("pro_reviews").delete().eq("pro_id", proId).eq("author_id", uid);
    if (error) throw error;
    return true;
  } catch (e) {
    console.error("deleteProReview:", e);
    return false;
  }
}

// Réponse du propriétaire. Le trigger pro_reviews_guard l'empêche de toucher à la
// note ou au texte de l'avis : il ne peut qu'ajouter sa réponse.
export async function replyProReview(reviewId, reply) {
  try {
    const { error } = await supabase.from("pro_reviews").update({ reply: reply || null }).eq("id", reviewId);
    if (error) throw error;
    return true;
  } catch (e) {
    console.error("replyProReview:", e);
    return false;
  }
}

// Contact d'un pro par la messagerie interne. N'a de sens que sur une fiche
// RÉCLAMÉE : sans pro.user_id il n'y a personne à qui écrire.
// La notification insérée déclenche les push (triggers trg_notify_push / trg_notify_fcm).
export async function contactPro(pro, message) {
  try {
    const auth = await supabase.auth.getUser();
    const me = auth?.data?.user;
    if (!me) return { ok: false, error: "not_signed_in" };
    const owner = pro && pro.user_id;
    if (!owner) return { ok: false, error: "no_owner" };
    if (owner === me.id) return { ok: false, error: "self" };

    const { data: existing } = await supabase
      .from("conversations").select("id").eq("pro_id", pro.id)
      .or("and(user1_id.eq." + me.id + ",user2_id.eq." + owner + "),and(user1_id.eq." + owner + ",user2_id.eq." + me.id + ")")
      .limit(1);

    let convId;
    if (existing && existing.length) {
      convId = existing[0].id;
    } else {
      const { data: conv, error: cErr } = await supabase.from("conversations").insert({
        user1_id: me.id, user2_id: owner, pro_id: pro.id,
        last_message: message.trim(), last_message_at: new Date().toISOString(),
      }).select().single();
      if (cErr) throw cErr;
      convId = conv.id;
    }

    const { error: mErr } = await supabase.from("messages").insert({
      conversation_id: convId, sender_id: me.id, content: message.trim(),
    });
    if (mErr) throw mErr;

    await supabase.from("conversations").update({
      last_message: message.trim(), last_message_at: new Date().toISOString(),
    }).eq("id", convId);

    const short = message.trim();
    await supabase.from("notifications").insert({
      user_id: owner, type: "message", title: "Nouveau message",
      message: short.length > 60 ? short.slice(0, 60) + "…" : short,
      read: false, link: "chat:" + convId,
    });

    return { ok: true, conversationId: convId };
  } catch (e) {
    console.error("contactPro:", e);
    return { ok: false, error: e.message };
  }
}

// Prévient le propriétaire d'une fiche qu'un avis vient d'être déposé. Silencieux :
// un échec de notification ne doit pas faire croire que l'avis n'est pas parti.
export async function notifyProReview(pro, rating) {
  try {
    if (!pro || !pro.user_id) return;
    const auth = await supabase.auth.getUser();
    if (auth?.data?.user?.id === pro.user_id) return;
    await supabase.from("notifications").insert({
      user_id: pro.user_id, type: "pro_review", title: "Nouvel avis sur votre fiche",
      message: "Vous avez reçu un avis " + rating + "/5 sur « " + (pro.name || "votre fiche") + " ».",
      read: false, link: "pro:" + pro.id,
    });
  } catch (e) {}
}

// Compteur d'ouvertures de fiche Pro. Mesure la marche manquante du tunnel d'amorçage :
// entre le mail délivré et la fiche réclamée, on ignorait si le pro cliquait seulement.
// Aucune donnée personnelle n'est enregistrée (ni IP, ni user agent, ni identité).
// Volontairement silencieux : un compteur ne doit jamais empêcher l'ouverture d'une fiche.
export async function logProLinkHit(proId, fromMail) {
  try {
    await supabase.rpc("log_pro_link_hit", { p_pro_id: proId, p_from_mail: !!fromMail });
  } catch (e) {}
}

// Suppression (soft-delete) de sa propre fiche Pro. RLS pros_update_own limite au propriétaire.
export async function deleteMyPro(proId) {
  try {
    // .select() -> si la RLS bloque, 0 ligne renvoyée (pas d'erreur PostgREST) : on le détecte.
    const { data, error } = await supabase.from("pros").update({ deleted_at: new Date().toISOString() }).eq("id", proId).select("id");
    if (error) throw error;
    return Array.isArray(data) && data.length > 0;
  } catch (e) {
    console.error("deleteMyPro:", e);
    return false;
  }
}

// Admin : supprime (soft-delete) une fiche Pro de l'annuaire.
// La policy RLS pros_update_own autorise déjà admin@horseconnex.com à UPDATE
// n'importe quelle fiche -> aucun RPC ni migration nécessaire.
export async function adminDeletePro(proId) {
  try {
    const { data, error } = await supabase.from("pros").update({ deleted_at: new Date().toISOString() }).eq("id", proId).select("id");
    if (error) throw error;
    return Array.isArray(data) && data.length > 0;
  } catch (e) {
    console.error("adminDeletePro:", e);
    return false;
  }
}

// Admin : liste tous les trajets HorseRoad actifs (la policy "Admins can manage
// all carpool trips" autorise la lecture complète).
export async function getAllTripsAdmin() {
  try {
    const { data, error } = await supabase
      .from("carpool_trips")
      .select("id, user_id, trip_type, trip_category, title, departure_city, departure_region, arrival_city, arrival_region, trip_date, price_per_horse, price_currency, created_at")
      .is("deleted_at", null)
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw error;
    return data || [];
  } catch (e) {
    console.error("getAllTripsAdmin:", e);
    return [];
  }
}

// Admin : supprime (soft-delete) un trajet HorseRoad. Policy admin ALL -> pas de RPC.
export async function adminDeleteTrip(tripId, reason = "Suppression par administration") {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.from("carpool_trips").update({
      deleted_at: new Date().toISOString(),
      deleted_by: user?.id || null,
      deletion_reason: reason,
      is_hidden: true,
    }).eq("id", tripId).select("id");
    if (error) throw error;
    return Array.isArray(data) && data.length > 0;
  } catch (e) {
    console.error("adminDeleteTrip:", e);
    return false;
  }
}

// Admin : bascule le badge "vérifié" d'une fiche Pro (RPC gardée par is_admin_user).
export async function adminSetProVerified(proId, v) {
  try {
    const { data, error } = await supabase.rpc("admin_set_pro_verified", { pro_id: proId, v });
    if (error) throw error;
    return data === true;
  } catch (e) {
    console.error("adminSetProVerified:", e);
    return false;
  }
}
